// Search, filter and sort for the Flohmarkt and member lists (pure functions, unit-tested)
import type { ArticleListItem, MemberCard } from './api';
import { stripMd } from './md';

export type ArticleSort = 'new' | 'asc' | 'desc';
const norm = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/ß/g, 'ss');

/** Pure filter/sort of the ad list (exported for tests) */
export function filterArticles(
  list: ArticleListItem[],
  f: { q: string; cat: string; mine: boolean; hideSold: boolean; sort: ArticleSort; me: number | undefined },
) {
  const toks = norm(f.q).split(/\s+/).filter(Boolean);
  const out = list.filter(
    a =>
      (!f.cat || a.category === f.cat) &&
      (!f.mine || a.seller_id === f.me) &&
      (!f.hideSold || a.status !== 'sold') &&
      (!toks.length ||
        toks.every(t =>
          norm(`${a.title} ${stripMd(a.description)} ${a.category} ${a.size} ${a.seller?.name ?? ''}`).includes(t),
        )),
  );
  const price = (a: ArticleListItem, none: number) => a.price ?? none;
  if (f.sort === 'asc') out.sort((a, b) => price(a, Infinity) - price(b, Infinity));
  else if (f.sort === 'desc') out.sort((a, b) => price(b, -1) - price(a, -1));
  return out;
}

/** Search, discipline filter and sorting of the member directory (exported for tests) */
export function filterMembers(
  list: MemberCard[],
  f: { q: string; discs: Set<string>; sort: 'active' | 'name'; neverOnly?: boolean },
) {
  const toks = norm(f.q).split(/\s+/).filter(Boolean);
  const out = list.filter(
    m =>
      (!f.neverOnly || !m.last_login_at) && // "Nie angemeldet" (admin view): no first login yet
      (!f.discs.size || [...f.discs].some(d => m.disciplines[d])) &&
      (!toks.length ||
        toks.every(t => norm(`${m.first_name} ${m.last_name} ${m.city ?? ''} ${m.email ?? ''}`).includes(t))),
  );
  const byName = (a: MemberCard, b: MemberCard) =>
    a.first_name.localeCompare(b.first_name, 'de') || a.last_name.localeCompare(b.last_name, 'de'); // by first name
  return out.sort(f.sort === 'active' ? (a, b) => b.total - a.total || byName(a, b) : byName);
}

export interface PickPerson {
  id: number;
  first_name: string;
  last_name: string;
}
/** "Vorname Nachname" – as everywhere in the app; club members know each other by first name */
export const pickLabel = (p: PickPerson) => `${p.first_name} ${p.last_name}`;
/**
 * Person picker: alphabetical by first name, then last name; typing matches the start of the first or the last name
 * (or of any part of a double name), umlauts and case do not matter ("muller" finds "Müller").
 */
export function matchPeople<T extends PickPerson>(list: T[], q: string, max = 50): T[] {
  const sorted = [...list].sort(
    (a, b) => a.first_name.localeCompare(b.first_name, 'de') || a.last_name.localeCompare(b.last_name, 'de'),
  );
  const toks = norm(q)
    .split(/[\s,-]+/)
    .filter(Boolean); // "anna-lena" like "anna lena"
  if (!toks.length) return sorted.slice(0, max);
  const words = (p: PickPerson) => norm(`${p.first_name} ${p.last_name}`).split(/[\s-]+/);
  return sorted.filter(p => toks.every(t => words(p).some(w => w.startsWith(t)))).slice(0, max);
}
